// ABOUTME: Hook that adds a delay after changesApplied transitions to true for auto-approved changes.
// This gives the backend time to propagate item-level data before showing the refresh button.

import {useEffect, useRef, useState} from 'react'

import {LatestOrderChangeRequest} from '../types/orderChangeBanner.types'

/**
 * Detects when changesApplied transitions to true for auto-approved changes
 * and delays the banner transition from "processing" to "changesApplied" by a configurable duration.
 *
 * This solves the issue where the hashsum-based changesApplied flag updates before
 * item-level data (e.g., quantity) is fully propagated in the backend.
 * Without this delay, users would refresh and still see old values.
 *
 * @returns true while the propagation delay is active (processing overlay should stay visible)
 */
export const usePropagationDelay = (
  latestRequest: LatestOrderChangeRequest | null | undefined,
  delayMs: number,
  orderId?: string
) => {
  const [isPropagating, setIsPropagating] = useState(false)
  const prevChangesAppliedRef = useRef<boolean | undefined>(undefined)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset all state when switching to a different order
  useEffect(() => {
    setIsPropagating(false)
    prevChangesAppliedRef.current = undefined
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [orderId])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    const requiresManualApproval = latestRequest?.requestData?.requiresManualApproval ?? false
    const changesApplied = latestRequest?.changesApplied
    const prevChangesApplied = prevChangesAppliedRef.current

    // Only apply propagation delay for auto-approved changes
    // Manual approval changes are handled by useAutoRefreshOnManualApproval
    if (!requiresManualApproval && changesApplied === true && prevChangesApplied === false) {
      setIsPropagating(true)
      timerRef.current = setTimeout(() => {
        setIsPropagating(false)
        timerRef.current = null
      }, delayMs)
    }

    prevChangesAppliedRef.current = changesApplied
  }, [latestRequest?.changesApplied, latestRequest?.requestData?.requiresManualApproval, delayMs])

  return isPropagating
}
